import { ChangeDetectionStrategy, Component, ElementRef, afterRenderEffect, computed, input, output, viewChild } from '@angular/core';
import { PaperButtonComponent, PaperDialogComponent, PaperIconComponent } from '@ricoh-paper/angular';

import type { ExactTimeMoveBooking, ExactTimeMoveContext } from '../../../core/models/desk-booking-change.model';
import { withClock } from '../../../core/utils/format-prefs';

/** Controlled exact-record chooser; permission and intervals come from the command owner. */
@Component({
  selector: 'im-desk-exact-time-move', standalone: true,
  imports: [PaperButtonComponent, PaperDialogComponent, PaperIconComponent],
  templateUrl: './desk-exact-time-move.component.html',
  styleUrls: ['../desk-slot-change-dialog/desk-slot-change-dialog.component.scss', './desk-exact-time-move.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ph-no-capture' },
})
export class DeskExactTimeMoveComponent {
  readonly context = input.required<ExactTimeMoveContext>();
  readonly saving = input(false);
  readonly selectBooking = output<string>();
  readonly confirmMove = output<void>();
  readonly closed = output<void>();
  protected readonly selected = computed(() => this.context().bookings.find(b => b.bookingId === this.context().selectedBookingId));
  private readonly receipt = viewChild<ElementRef<HTMLElement>>('receiptHeading');
  constructor() {
    afterRenderEffect(() => { if (this.context().saved) this.receipt()?.nativeElement.focus(); });
  }
  /** "Ended" / "Started" for a booking whose time has passed — the reason a move is refused, shown before the server says so. */
  protected phase(booking: ExactTimeMoveBooking): string | null {
    const now = Date.now();
    const end = Date.parse(booking.endTime);
    const start = Date.parse(booking.startTime);
    if (Number.isFinite(end) && end <= now) return 'Ended';
    if (Number.isFinite(start) && start <= now) return 'Started';
    return null;
  }
  protected range(booking: ExactTimeMoveBooking): string {
    const format = new Intl.DateTimeFormat('en-GB', withClock({ timeZone: this.context().timezone,
      day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }));
    return [booking.startTime, booking.endTime].map(value => format.format(new Date(value)).replace(/\s+(am|pm)/g, '$1')).join(' – ');
  }
}
